import React, { useState } from 'react';
import { Bid } from '../../types/bidding';
import { useCRM } from '../../utils/CRMContext';
import { formatCurrency } from '../../utils/bidCalculator';
import BidGroupEditor from './BidGroupEditor';
import BidProposalEditor from './BidProposalEditor';
import { Pencil } from 'lucide-react';
import './BidDetailsPanel.css';

interface BidDetailsPanelProps {
  bid: Bid;
  onClose: () => void;
  onEdit?: (bid: Bid) => void;
}

const BidDetailsPanel: React.FC<BidDetailsPanelProps> = ({ bid, onClose, onEdit }) => {
  const { updateBid } = useCRM();
  const [activeTab, setActiveTab] = useState<'summary' | 'groups' | 'proposal'>('summary');
  const [isEditing, setIsEditing] = useState(false);

  const handleUpdateBid = async (updates: Partial<Bid>) => {
    try {
      await updateBid(bid.id, updates);
    } catch (error) {
      console.error('Error updating bid:', error);
      alert('Failed to update bid');
    }
  };

  const grandTotal = bid.groups.reduce((sum, group) => sum + group.total, 0);

  return (
    <div className="bid-details-panel">
      <div className="panel-header">
        <div className="header-left">
          <h2>{bid.bidNumber}</h2>
          <span className={`status-badge status-${bid.status}`}>
            {bid.status.replace('-', ' ')}
          </span>
        </div>
        <div className="header-right">
          <div className="grand-total-display">
            {formatCurrency(grandTotal)}
          </div>
          {onEdit && (
            <button 
              className="edit-bid-btn" 
              onClick={() => onEdit(bid)}
              title="Edit Bid"
            >
              <Pencil size={16} /> Edit
            </button>
          )}
          <button className="close-panel-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="panel-tabs">
        <button
          className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button
          className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Groups ({bid.groups.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'proposal' ? 'active' : ''}`}
          onClick={() => setActiveTab('proposal')}
        >
          Proposal
        </button>
      </div>

      <div className="panel-content">
        {activeTab === 'summary' && (
          <div className="summary-tab">
            <div className="info-section">
              <h3>Project Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Project Name:</span>
                  <span className="info-value">{bid.projectName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created:</span>
                  <span className="info-value">{new Date(bid.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Version:</span>
                  <span className="info-value">v{bid.version}</span>
                </div>
              </div>
            </div>

            <div className="groups-summary">
              <h3>Groups Overview</h3>
              {bid.groups.map((group, index) => (
                <div key={group.id} className="group-summary-card">
                  <div className="group-summary-header">
                    <span className="group-summary-name">{group.name}</span>
                    <span className="group-summary-total">{formatCurrency(group.total)}</span>
                  </div>
                  <div className="group-summary-details">
                    <span>{group.timberList.length} timber items</span>
                    <span>•</span>
                    <span>{group.productionBucket.totalBoardFeet.toFixed(2)} BF</span>
                    <span>•</span>
                    <span>{(group.profitMargin * 100).toFixed(0)}% margin</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="totals-summary">
              <div className="total-row">
                <span>Subtotal (all groups):</span>
                <span>{formatCurrency(bid.groups.reduce((sum, g) => sum + g.subtotal, 0))}</span>
              </div>
              <div className="total-row grand-total-row">
                <span><strong>Grand Total:</strong></span>
                <span><strong>{formatCurrency(grandTotal)}</strong></span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="groups-tab">
            <BidGroupEditor
              groups={bid.groups}
              onUpdate={(groups) => handleUpdateBid({ groups })}
              readOnly={!isEditing}
            />
            {!isEditing && (
              <button className="edit-btn" onClick={() => setIsEditing(true)}>
                Edit Costs
              </button>
            )}
            {isEditing && (
              <button className="done-btn" onClick={() => setIsEditing(false)}>
                Done Editing
              </button>
            )}
          </div>
        )}

        {activeTab === 'proposal' && (
          <div className="proposal-tab">
            <BidProposalEditor bid={bid} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BidDetailsPanel;
